import React from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../registry/PropertyEditor.types';

function renderButtonEditor(props: PropertyEditorProps) {
  const { control, onConfigChange } = props;

  // Derive logical behavior from config keys that ButtonControl actually reads:
  // config.type === 'submit'  → submit form
  // config.action === 'navigate' → navigate to URL (config.url, config.target)
  // otherwise → plain button
  const configType = (control.config?.type as string) || 'button';
  const configAction = (control.config?.action as string) || '';

  type Behavior = 'submit' | 'navigate' | 'button';
  const behavior: Behavior =
    configType === 'submit' ? 'submit' :
    configAction === 'navigate' ? 'navigate' :
    'button';

  function setBehavior(next: Behavior) {
    if (next === 'submit') {
      onConfigChange('type', 'submit');
      onConfigChange('action', undefined);
      onConfigChange('url', undefined);
    } else if (next === 'navigate') {
      onConfigChange('type', 'button');
      onConfigChange('action', 'navigate');
    } else {
      onConfigChange('type', 'button');
      onConfigChange('action', undefined);
      onConfigChange('url', undefined);
    }
  }

  return (
    <div className="af-properties-tab__section">
      <h4 className="af-properties-tab__subsection-title">Button Settings</h4>
      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label">
          Variant
          <select
            value={(control.config?.variant as string) || 'primary'}
            onChange={(e) => onConfigChange('variant', e.target.value)}
            className="af-properties-tab__select"
          >
            <option value="primary">Primary</option>
            <option value="secondary">Secondary</option>
            <option value="danger">Danger</option>
            <option value="default">Default</option>
            <option value="success">Success</option>
            <option value="warning">Warning</option>
            <option value="ghost">Ghost</option>
            <option value="link">Link</option>
          </select>
        </label>
      </div>

      <h4 className="af-properties-tab__subsection-title" style={{ marginTop: '16px' }}>Click Behavior</h4>
      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label">
          Action
          <select
            value={behavior}
            onChange={(e) => setBehavior(e.target.value as Behavior)}
            className="af-properties-tab__select"
          >
            <option value="button">None (plain button)</option>
            <option value="submit">Submit Form</option>
            <option value="navigate">Navigate to URL</option>
          </select>
        </label>
      </div>

      {behavior === 'navigate' && (
        <>
          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              URL
              <input
                type="text"
                value={(control.config?.url as string) || ''}
                onChange={(e) => onConfigChange('url', e.target.value || undefined)}
                className="af-properties-tab__input"
                placeholder="https://..."
              />
            </label>
          </div>
          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Open In
              <select
                value={(control.config?.target as string) || '_self'}
                onChange={(e) => onConfigChange('target', e.target.value)}
                className="af-properties-tab__select"
              >
                <option value="_self">Same Tab</option>
                <option value="_blank">New Tab</option>
              </select>
            </label>
          </div>
        </>
      )}
    </div>
  );
}

export const ButtonEditor: PropertyEditor = {
  type: 'button',
  label: 'Button',
  description: 'Configure button variant and click behavior',
  render: renderButtonEditor,
};
